/* eslint-disable camelcase */
import { RequestMethod, DATA_RANGE_HEADER_KEY, parseDataRange, } from '@moderepo/mode-apis-base';
import { AppProxyAPI, } from './AppProxyAPI';
/**
 * How to robot communicate with MODE cloud, direct or through a gateway
 */
export var RobotCommunicationType;
(function (RobotCommunicationType) {
    RobotCommunicationType["DIRECT"] = "direct";
    RobotCommunicationType["GATEWAY"] = "gateway";
})(RobotCommunicationType || (RobotCommunicationType = {}));
/**
 * The list of known numeric metric types. We need a list of these metric type so that we can create more specific feature for them.
 * NOTE: NumericMetricType is NOT the same as metrics' valueName. For example, a NumericMetricType can be 'motor_speed' and a robot can have more
 * than 1 metrics of this type and they can have valueNames like 'wheel_motor_speed', 'arm_motor_speed', 'head_motor_speed'.
 * NumericMetricType will be standard (hopefully) but metrics' valueName can be anything the robot manufacturer want to call. Therefore we can only
 * define constants for NumericMetricType but not metric value names.
 */
export var RCNumericMetricType;
(function (RCNumericMetricType) {
    RCNumericMetricType["BATTERY_LEVEL"] = "battery_level";
    RCNumericMetricType["BATTERY_LIFE"] = "battery_life";
    RCNumericMetricType["CONNECTION"] = "connection";
    RCNumericMetricType["SPEED"] = "speed";
    RCNumericMetricType["ACCELERATION"] = "acceleration";
    RCNumericMetricType["RMF"] = "rmf";
    RCNumericMetricType["NETWORK"] = "network";
    RCNumericMetricType["RADAR"] = "radar";
    RCNumericMetricType["LATITUDE"] = "latitude";
    RCNumericMetricType["LONGITUDE"] = "longitude";
    RCNumericMetricType["ALTITUDE"] = "altitude";
    RCNumericMetricType["FLOOR"] = "floor";
    RCNumericMetricType["WEIGHT"] = "weight";
    RCNumericMetricType["DOOR"] = "door";
    RCNumericMetricType["MOTOR_LIFE"] = "motor_life";
})(RCNumericMetricType || (RCNumericMetricType = {}));
/**
 * Most Metrics' valueNames are not standard EXCEPT for these metrics. We need to standardize these valueNames so that we can use them in the app.
 * If they are not standardized, we won't know how to use them. So below are the list of standardized metrics' value names.
 */
export var RCNumericMetricValueName;
(function (RCNumericMetricValueName) {
    RCNumericMetricValueName["LATITUDE"] = "gps_lat";
    RCNumericMetricValueName["LONGITUDE"] = "gps_lng";
    RCNumericMetricValueName["ALTITUDE"] = "gps_alt";
})(RCNumericMetricValueName || (RCNumericMetricValueName = {}));
export var MonitorActionType;
(function (MonitorActionType) {
    MonitorActionType["CREATE_ISSUE"] = "Issue";
    MonitorActionType["SEND_EMAIL"] = "Email";
    MonitorActionType["CREATE_TICKET"] = "Ticket";
    MonitorActionType["SLACK_MESSAGE"] = "Slack";
})(MonitorActionType || (MonitorActionType = {}));
const MONITOR_SEVERITY_MINOR = 1000;
const MONITOR_SEVERITY_WARN = 6000;
const MONITOR_SEVERITY_CRITICAL = 11000;
/**
 * Set of possible severity level a monitor can have
 */
export var MonitorSeverity;
(function (MonitorSeverity) {
    MonitorSeverity[MonitorSeverity["MINOR"] = MONITOR_SEVERITY_MINOR] = "MINOR";
    MonitorSeverity[MonitorSeverity["WARN"] = MONITOR_SEVERITY_WARN] = "WARN";
    MonitorSeverity[MonitorSeverity["CRITICAL"] = MONITOR_SEVERITY_CRITICAL] = "CRITICAL";
})(MonitorSeverity || (MonitorSeverity = {}));
/**
 * Device's heath is based on Monitor's severity. For example, if a device has a monitor with CRITICAL severity then the robot's health is in critical
 * condition. Therefore, we should use the same value as MonitorSeverity for RobotHealthCondition enum so that we can easily compare them.
 */
export var RobotHealthCondition;
(function (RobotHealthCondition) {
    RobotHealthCondition[RobotHealthCondition["UNKNOWN"] = -1] = "UNKNOWN";
    RobotHealthCondition[RobotHealthCondition["NORMAL"] = 0] = "NORMAL";
    RobotHealthCondition[RobotHealthCondition["MINOR"] = MONITOR_SEVERITY_MINOR] = "MINOR";
    RobotHealthCondition[RobotHealthCondition["WARN"] = MONITOR_SEVERITY_WARN] = "WARN";
    RobotHealthCondition[RobotHealthCondition["CRITICAL"] = MONITOR_SEVERITY_CRITICAL] = "CRITICAL";
})(RobotHealthCondition || (RobotHealthCondition = {}));
export var TicketSystemType;
(function (TicketSystemType) {
    TicketSystemType["SALESFORCE"] = "SalesForce";
    TicketSystemType["ASANA"] = "Asana";
    TicketSystemType["JIRA"] = "Jira";
})(TicketSystemType || (TicketSystemType = {}));
export var ComparisonOperator;
(function (ComparisonOperator) {
    ComparisonOperator["LESS_THAN"] = "lt";
    ComparisonOperator["LESS_THAN_OR_EQUAL"] = "le";
    ComparisonOperator["EQUAL"] = "eq";
    ComparisonOperator["GREATER_THAN_OR_EQUAL"] = "ge";
    ComparisonOperator["GREATER_THAN"] = "gt";
})(ComparisonOperator || (ComparisonOperator = {}));
export var LogicalOperator;
(function (LogicalOperator) {
    LogicalOperator["AND"] = "and";
    LogicalOperator["OR"] = "or";
})(LogicalOperator || (LogicalOperator = {}));
export var MonitorConditionThresholdType;
(function (MonitorConditionThresholdType) {
    MonitorConditionThresholdType["CONTINUOUS_COUNT"] = "continuousCount";
    MonitorConditionThresholdType["DURATION_COUNT"] = "durationCount";
})(MonitorConditionThresholdType || (MonitorConditionThresholdType = {}));
export var MonitorTargetType;
(function (MonitorTargetType) {
    MonitorTargetType["ROBOT_CLASS"] = "RobotClass";
    MonitorTargetType["ROBOT_ID"] = "RobotId";
})(MonitorTargetType || (MonitorTargetType = {}));
export var MissingDataTreatment;
(function (MissingDataTreatment) {
    MissingDataTreatment["SATISFIED"] = "satisfied";
    MissingDataTreatment["UNSATISFIED"] = "unsatisfied";
    MissingDataTreatment["IGNORED"] = "ignored";
})(MissingDataTreatment || (MissingDataTreatment = {}));
/* -------------------------------  BEGIN MODELS FOR FILE REQUEST FEATURE  ---------------------------------------- */
export var FileRequestStatus;
(function (FileRequestStatus) {
    FileRequestStatus["INITIATED"] = "initiated";
    FileRequestStatus["PENDING"] = "pending";
    FileRequestStatus["RECEIVING"] = "receiving";
    FileRequestStatus["SUCCEEDED"] = "succeeded";
    FileRequestStatus["WARNING"] = "warning";
    FileRequestStatus["FAILED"] = "failed";
    FileRequestStatus["TIME_OUT"] = "timed_out";
    FileRequestStatus["CANCELLING"] = "cancelling";
    FileRequestStatus["CANCELED"] = "canceled";
    FileRequestStatus["NOT_FOUND"] = "not_found";
})(FileRequestStatus || (FileRequestStatus = {}));
export var FileRequestTarget;
(function (FileRequestTarget) {
    FileRequestTarget["ROBOT"] = "robot";
    FileRequestTarget["DEVICE"] = "device";
    FileRequestTarget["CONTROL_SERVER"] = "control_server";
    FileRequestTarget["CLOUD"] = "cloud";
})(FileRequestTarget || (FileRequestTarget = {}));
export var FileRequestAction;
(function (FileRequestAction) {
    FileRequestAction["UPLOAD_TO_ROBOT"] = "cloud_to_robot";
    FileRequestAction["UPLOAD_TO_DEVICE"] = "cloud_to_device";
    FileRequestAction["UPLOAD_TO_CONTROL_SERVER"] = "cloud_to_control_server";
    FileRequestAction["DOWNLOAD_FROM_ROBOT"] = "robot_to_cloud";
    FileRequestAction["DOWNLOAD_FROM_DEVICE"] = "device_to_cloud";
    FileRequestAction["DOWNLOAD_FROM_CONTROL_SERVER"] = "control_server_to_cloud";
    FileRequestAction["CANCEL"] = "cancel";
})(FileRequestAction || (FileRequestAction = {}));
/**
 * Type guard function to check if the given object is a FileRequest
 * @param object
 * @returns
 */
export const isFileRequest = (object) => {
    const request = object;
    return request && typeof request.id === 'number' && typeof request.homeId === 'number' && typeof request.deviceId === 'number'
        && Object.values(FileRequestTarget).includes(request.target)
        && Object.values(FileRequestAction).includes(request.action)
        && Object.values(FileRequestStatus).includes(request.status)
        && typeof request.createdAt === 'string'
        && typeof request.updatedAt === 'string';
};
/**
 * Type guard function to check if the given object is a DownloadFileRequest
 * @param object
 * @returns
 */
export const isDownloadFileRequest = (object) => {
    const request = object;
    return request && isFileRequest(request)
        && (request.action === FileRequestAction.DOWNLOAD_FROM_DEVICE
            || request.action === FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER
            || request.action === FileRequestAction.DOWNLOAD_FROM_ROBOT)
        && request.parameters
        && typeof request.parameters.fileType === 'string'
        && request.parameters.fetchFileParameters
        && typeof request.parameters.fetchFileParameters.begin === 'string'
        && typeof request.parameters.fetchFileParameters.end === 'string';
};
/**
 * Type guard function to check if the given object is a UploadFileRequest
 * @param object
 * @returns
 */
export const isUploadFileRequest = (object) => {
    const request = object;
    return request && isFileRequest(request)
        && (request.action === FileRequestAction.UPLOAD_TO_DEVICE
            || request.action === FileRequestAction.UPLOAD_TO_CONTROL_SERVER
            || request.action === FileRequestAction.UPLOAD_TO_ROBOT)
        && request.parameters
        && (request.parameters.fileType === undefined || typeof request.parameters.fileType === 'string')
        && typeof request.parameters.fileName === 'string'
        && typeof request.parameters.fileSetSize === 'number'
        && typeof request.parameters.fileSetChecksum === 'string';
};
/* -------------------------------  END MODELS FOR FILE REQUEST FEATURE  ---------------------------------------- */
/**
 * This is the class used for making API call to Robot Cloud server. Robot Cloud server uses App Proxy therefore we will be making the RC API call
 * through Maze, AppAPI.
 */
export class RobotCloudAPI extends AppProxyAPI {
    /**
     * Return an Instance of RobotCloudAPI. This is what dev should be using to get an instance of RobotCloudAPI
     * and use it to make API call.
     */
    static getInstance() {
        if (!RobotCloudAPI.rcInstance) {
            RobotCloudAPI.rcInstance = new RobotCloudAPI();
        }
        return RobotCloudAPI.rcInstance;
    }
    /**
     * Load all robot definitions for a specific project
     * @param projectId
     */
    async getRobotCatalog(projectId) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/robotDefinitions`);
        if (response.data instanceof Array) {
            return response.data;
        }
        return [];
    }
    /**
     * TODO - Remove this function because it shouldn't be done by Ritz. This should be done by some other tools
     *
     * Add RobotDefinition
     * @param projectId
     * @param robotDef
     */
    async createRobotDefinition(projectId, robotDef) {
        await this.sendRequest(RequestMethod.POST, `projects/${projectId}/appProxy/rcp/robotDefinitions`, robotDef);
    }
    /**
     * TODO - Remove this function because it shouldn't be done by Ritz. This should be done by some other tools
     *
     * Update RobotDefinition
     * @param projectId
     * @param robotClass
     * @param robotDef
     */
    async updateRobotDefinition(projectId, robotClass, robotDef) {
        await this.sendRequest(RequestMethod.PATCH, `projects/${projectId}/appProxy/rcp/robotDefinitions/${robotClass}`, robotDef);
    }
    /**
     * Get the list of Monitors created for a project
     * @param projectId
     * @param skip
     * @param limit
     */
    // TODO - Make skip/limit optional
    async getMonitors(projectId, skip, limit, filters) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/alertConditions`, {
            limit, skip, ...filters,
        });
        if (response.data instanceof Array) {
            return {
                range: parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]),
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get 1 Monitor by id
     * @param projectId
     * @param id
     */
    async getMonitor(projectId, id) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/alertConditions/${id}`);
        return response.data;
    }
    async createMonitor(projectId, agentId, data) {
        const response = await this.sendRequest(RequestMethod.POST, `projects/${projectId}/appProxy/rcp/alertConditions`, {
            ...data,
            agentId,
            projectId,
        });
        return response.data;
    }
    async updateMonitor(projectId, agentId, monitorId, data) {
        await this.sendRequest(RequestMethod.PATCH, `projects/${projectId}/appProxy/rcp/alertConditions/${monitorId}`, {
            ...data,
            agentId,
            projectId,
        });
    }
    /**
     * Delete a monitor by id
     * @param projectId
     * @param agentId
     * @param id
     */
    async deleteMonitor(projectId, id) {
        await this.sendRequest(RequestMethod.DELETE, `projects/${projectId}/appProxy/rcp/alertConditions/${id}`);
    }
    /**
     * Get the list of Issues created for a project
     * @param projectId
     * @param filters - The search filters for Issues
     */
    async getIssues(projectId, filters) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/issues`, filters);
        if (response.data instanceof Array) {
            return {
                range: parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]),
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get 1 Issue by id
     * @param projectId
     * @param id
     */
    async getIssue(projectId, id) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/issues/${id}`);
        return response.data;
    }
    /**
     * Create an Issue for the specified home. NOTE: homeId is not part of the Issue data therefore it is not included in the data object.
     */
    async createIssue(projectId, agentId, data) {
        const response = await this.sendRequest(RequestMethod.POST, `projects/${projectId}/appProxy/rcp/agents/${agentId}/issues`, {
            homeId: data.homeId,
            deviceId: data.deviceId,
            robotId: data.robotId,
            severity: data.severity,
            description: data.description,
            ticketLink: data.ticketLink,
            resolvedAt: null,
        });
        return response.data;
    }
    /**
     * Update an Issue
     * @param projectId
     * @param agentId
     * @param issueId
     * @param data
     */
    async updateIssue(projectId, agentId, issueId, data) {
        await this.sendRequest(RequestMethod.PATCH, `projects/${projectId}/appProxy/rcp/issues/${issueId}`, {
            description: data.description,
            severity: data.severity,
            ticketLink: data.ticketLink,
            resolvedAgentId: data.resolvedAgentId,
            resolvedAt: data.resolvedAgentId !== undefined ? (new Date()).toISOString() : undefined,
            unresolved: data.unresolved,
            agentId,
            projectId,
        });
    }
    /**
     * Delete an issue by id
     */
    async deleteIssue(projectId, id) {
        await this.sendRequest(RequestMethod.DELETE, `projects/${projectId}/appProxy/rcp/issues/${id}`);
    }
    /**
     * Get all Comments belonged to a specific issue
     * @param projectId
     * @param issueId
     */
    async getAllIssueComments(projectId, issueId) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/issues/${issueId}/comments`);
        if (response.data instanceof Array) {
            return {
                range: parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]),
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get 1 Comment from a specific issue
     * @param projectId - The id of the project the issue belonged to
     * @param issueId - The id of the issue the comment belonged to
     * @param commentId - The id of the comment to fetch
     */
    async getIssueComment(projectId, issueId, commentId) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/issues/${issueId}/comments/${commentId}`);
        return response.data;
    }
    /**
     * Add a comment to an issue
     * @param projectId - The id of the project the issue belonged to
     * @param issueId - The id of the issue the comment belonged to
     * @param agentId - The id of the agent that make the comment
     * @param message - The message for the comment
     */
    async addCommentForIssue(projectId, issueId, agentId, message) {
        await this.sendRequest(RequestMethod.POST, `projects/${projectId}/appProxy/rcp/issues/${issueId}/comments`, {
            agentId, message,
        });
    }
    /**
     * Update a comment for an issue
     * @param projectId - The id of the project the issue belonged to
     * @param issueId - The id of the issue the comment belonged to
     * @param commentId - The id of the comment to update
     * @param message - The updated message
     */
    async updateCommentForIssue(projectId, issueId, commentId, message) {
        await this.sendRequest(RequestMethod.PATCH, `projects/${projectId}/appProxy/rcp/issues/${issueId}/comments/${commentId}`, {
            message,
        });
    }
    /**
     * Delete a comment from an issue
     * @param projectId - The id of the project the issue belonged to
     * @param issueId - The id of the issue the comment belonged to
     * @param commentId - The id of the comment to delete
     */
    async deleteCommentFromIssue(projectId, issueId, commentId) {
        await this.sendRequest(RequestMethod.DELETE, `projects/${projectId}/appProxy/rcp/issues/${issueId}/comments/${commentId}`);
    }
    /**
     * Get RobotCloud project settings
     * @param projectId
     */
    async getProjectSettings(projectId) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/settings`);
        return response.data;
    }
    /**
     * Update RobotCloud ProjectSettings
     * @param projectId
     * @param settings
     */
    async updateProjectSettings(projectId, settings) {
        await this.sendRequest(RequestMethod.PATCH, `projects/${projectId}/appProxy/rcp/settings`, settings);
    }
    /**
     * Get a device's health status
     * @param homeId
     * @param robotId
     */
    async getRobotStatus(homeId, robotId) {
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/appProxy/rcp/devices/${robotId}/robotStatus`);
        return response.data;
    }
    /**
     * Get a device's health status for a specified list of devices
     * @param homeId
     * @param robotIds
     */
    async getRobotStatusesByIds(homeId, robotIds) {
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/appProxy/rcp/robotStatuses`, {
            robotIds: `${robotIds.join(',')}`,
        });
        return response.data;
    }
    /**
     * Get the list of robots created for a project
     * @param projectId
     * @param skip
     * @param limit
     */
    async getRobots(projectId, filters) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/robots`, filters);
        // if there is data returned, check to see if it is NEW data format or old data format
        // If old format, response.data is an array of RobotInfo
        // If new format, response.data is an array of objects which contains Robot and Status
        const formattedData = response.data
            ? response.data.map((data) => {
                if (data.robot) {
                    // new format, data is an object that contain robot and status. We will combine them into 1 RobotInfo object by placing
                    // the "status" object inside of RobotInfo object
                    return {
                        ...data.robot,
                        status: data.status,
                    };
                }
                return data; // old format, data IS a RobotInfo
            })
            : []; // Make sure it is an empty array if there is no robot
        return {
            range: parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]),
            items: formattedData,
        };
    }
    /**
     * Get 1 Robot by id
     * @param homeId
     * @param id
     */
    async getRobotById(homeId, id) {
        var _a;
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/appProxy/rcp/robots/${id}`);
        // if there is data returned, check to see if it is NEW data format or old data format
        // If old format, response.data is a RobotInfo object
        // If new format, response.data is an objects which contains Robot and Status
        const formattedData = ((_a = response.data) === null || _a === void 0 ? void 0 : _a.robot)
            ? {
                ...response.data.robot,
                status: response.data.status,
            }
            : response.data;
        return formattedData;
    }
    /**
     * Create a Robot with the specified robotData and add it to the given homeId
     * @param homeId
     * @param robotData
     */
    async createRobot(homeId, robotData) {
        const response = await this.sendRequest(RequestMethod.POST, `homes/${homeId}/appProxy/rcp/robots`, {
            ...robotData, homeId,
        });
        return response.data;
    }
    /**
     * Update a Robot
     * @param homeId
     * @param robotId
     * @param robotData
     */
    async updateRobot(homeId, robotId, robotData) {
        await this.sendRequest(RequestMethod.PATCH, `homes/${homeId}/appProxy/rcp/robots/${robotId}`, robotData);
    }
    /**
     * Delete a Robot
     * @param homeId
     * @param robotId
     */
    async deleteRobot(homeId, robotId) {
        await this.sendRequest(RequestMethod.DELETE, `homes/${homeId}/appProxy/rcp/robots/${robotId}`);
    }
    /**
     * Load all Gateway definitions for a specific project
     * @param projectId
     */
    async getGatewayCatalog(projectId) {
        const response = await this.sendRequest(RequestMethod.GET, `projects/${projectId}/appProxy/rcp/deviceDefinitions`);
        return response.data;
    }
    /**
     * Get ONE file request by request ID. This can be used for fetching both file Download or Upload requests
     * @param homeId
     * @param requestId
     * @returns
     */
    async getFileRequestById(homeId, requestId) {
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/appProxy/rcp/fileProcessor/requests/${requestId}`);
        return response.data;
    }
    /**
     * Get ALL file requests filtered by the specified filters
     * @param homeId
     * @param filters
     * @returns
     */
    async getAllFileRequests(homeId, filters) {
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/appProxy/rcp/fileProcessor/requests`, filters);
        if (response.data instanceof Array) {
            return {
                range: parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]),
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get the download URL for a DOWNLOAD file transfer request. Once a download file request is ready and the file is pushed to S3, the agent
     * can make a request to get the download link and start downloading the file.
     * NOTE: this link has an expiration time so it can not be cached and reused for a very long time. Maybe 1 day max.
     * @param homeId
     * @param requestId
     * @returns
     */
    async getDownloadFileRequestDownloadURL(homeId, requestId) {
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/appProxy/rcp/fileProcessor/requests/${requestId}/d2c`);
        return response.data;
    }
    /**
     * Create a request to UPLOAD a file to a DEVICE or CONTROL SERVER. This will only create a request and return a URL to S3 which the
     * caller need to call to upload the file to. This API does not upload any file.
     * @param homeId
     * @param deviceId
     * @returns
     */
    async createUploadFileRequest(homeId, deviceId, fileName, useCustomDriver, action, target) {
        const response = await this.sendRequest(RequestMethod.POST, `homes/${homeId}/appProxy/rcp/fileProcessor/requests`, {
            homeId,
            deviceId,
            target,
            action,
            fileName,
            useCustomDriver,
        });
        return response.data;
    }
    /**
     * Create a request to UPLOAD a file to a DEVICE. This will only create a request and return a URL to S3 which the caller need to call
     * to upload the file to. This API does not upload any file.
     * @param homeId
     * @param deviceId
     * @returns
     */
    async createUploadFileToDeviceRequest(homeId, deviceId, fileName, useCustomDriver) {
        return this.createUploadFileRequest(homeId, deviceId, fileName, useCustomDriver, FileRequestAction.UPLOAD_TO_DEVICE, FileRequestTarget.DEVICE);
    }
    /**
     * Create a request to UPLOAD a file to a CONTROL SERVER. This will only create a request and return a URL to S3 which the caller need to call
     * to upload the file to. This API does not upload any file.
     * @param homeId
     * @param deviceId
     * @returns
     */
    async createUploadFileToControlServerRequest(homeId, deviceId, fileName, useCustomDriver) {
        return this.createUploadFileRequest(homeId, deviceId, fileName, useCustomDriver, FileRequestAction.UPLOAD_TO_CONTROL_SERVER, FileRequestTarget.CONTROL_SERVER);
    }
    /**
     * Create a request to UPLOAD a file to all ROBOTS. This will only create a request and return a URL to S3 which the caller need to call
     * to upload the file to. This API does not upload any file.
     * @param homeId
     * @param deviceId
     * @returns
     */
    async createUploadFileToRobotsRequest(homeId, deviceId, fileName, useCustomDriver) {
        const response = await this.sendRequest(RequestMethod.POST, `homes/${homeId}/appProxy/rcp/fileProcessor/requests`, {
            homeId,
            deviceId,
            target: FileRequestTarget.ROBOT,
            action: FileRequestAction.UPLOAD_TO_ROBOT,
            fileName,
            useCustomDriver,
        });
        return response.data;
    }
    /**
     * Once a file is uploaded to S3 successfully from frontend, call this API to update the file upload request's status and metadata. This
     * will notify the backend that the file is already uploaded S3 and now it can start download that file to the DEVICE or CONTROL SERVER
     * @param homeId
     * @param requestId
     * @param params
     */
    async updateUploadFileRequestMetadata(homeId, requestId, params, action) {
        await this.sendRequest(RequestMethod.PATCH, `homes/${homeId}/appProxy/rcp/fileProcessor/requests/${requestId}/c2d-browser`, {
            status: FileRequestStatus.PENDING,
            action,
            parameters: {
                ...params,
            },
        });
    }
    /**
     * Once a file is uploaded to S3 successfully from frontend, call this API to update the file upload request's status and metadata. This
     * will notify the backend that the file is already uploaded S3 and now it can start download that file to the DEVICE
     * @param homeId
     * @param requestId
     * @param params
     */
    async updateUploadFileToDeviceRequestMetadata(homeId, requestId, params) {
        return this.updateUploadFileRequestMetadata(homeId, requestId, params, FileRequestAction.UPLOAD_TO_DEVICE);
    }
    /**
     * Once a file is uploaded to S3 successfully from frontend, call this API to update the file upload request's status and metadata. This
     * will notify the backend that the file is already uploaded S3 and now it can start download that file to the CONTROL SERVER
     * @param homeId
     * @param requestId
     * @param params
     */
    async updateUploadFileToControlServerRequestMetadata(homeId, requestId, params) {
        return this.updateUploadFileRequestMetadata(homeId, requestId, params, FileRequestAction.UPLOAD_TO_CONTROL_SERVER);
    }
    /**
     * Once a file is uploaded to S3 successfully from frontend, call this API to update the file upload request's status and metadata. This
     * will notify the backend that the file is already uploaded S3 and now it can start download that file to the ROBOTS
     * @param homeId
     * @param requestId
     * @param params
     */
    async updateUploadFileToRobotsRequestMetadata(homeId, requestId, robotId, params) {
        await this.sendRequest(RequestMethod.PATCH, `homes/${homeId}/appProxy/rcp/fileProcessor/requests/${requestId}/c2d-browser`, {
            status: FileRequestStatus.PENDING,
            action: FileRequestAction.UPLOAD_TO_ROBOT,
            robotId,
            parameters: {
                ...params,
            },
        });
    }
    /**
     * Create a request to DOWNLOAD a file from a DEVICE or CONTROL SERVER
     * @param homeId
     * @param deviceId
     * @param fileType
     * @returns
     */
    async createDownloadFileRequest(homeId, deviceId, fileType, startDate, endDate, useCustomDriver, action) {
        const response = await this.sendRequest(RequestMethod.POST, `homes/${homeId}/appProxy/rcp/fileProcessor/requests`, {
            homeId,
            deviceId,
            target: FileRequestTarget.CLOUD,
            action,
            useCustomDriver,
            parameters: {
                fileType,
                fetchFileParameters: {
                    begin: startDate,
                    end: endDate,
                },
            },
        });
        return response;
    }
    /**
     * Create a request to DOWNLOAD a file from a DEVICE
     * @param homeId
     * @param deviceId
     * @param fileType
     * @param fetchFileParameters
     * @returns
     */
    async createDownloadFileFromDeviceRequest(homeId, deviceId, fileType, startDate, endDate, useCustomDriver) {
        return this.createDownloadFileRequest(homeId, deviceId, fileType, startDate, endDate, useCustomDriver, FileRequestAction.DOWNLOAD_FROM_DEVICE);
    }
    /**
     * Create a request to DOWNLOAD a file from a CONTROL SERVER
     * @param homeId
     * @param deviceId
     * @param fileType
     * @param fetchFileParameters
     * @returns
     */
    async createDownloadFileFromControlServerRequest(homeId, deviceId, fileType, startDate, endDate, useCustomDriver) {
        return this.createDownloadFileRequest(homeId, deviceId, fileType, startDate, endDate, useCustomDriver, FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER);
    }
    /**
     * Create a request to DOWNLOAD file from a ROBOT
     * @param homeId
     * @param deviceId
     * @param robotId
     * @param fileType
     * @param fetchFileParameters
     * @returns
     */
    async createDownloadFileFromRobotRequest(homeId, deviceId, robotId, fileType, startDate, endDate, useCustomDriver) {
        const response = await this.sendRequest(RequestMethod.POST, `homes/${homeId}/appProxy/rcp/fileProcessor/requests`, {
            homeId,
            deviceId,
            robotId,
            target: FileRequestTarget.CLOUD,
            action: FileRequestAction.DOWNLOAD_FROM_ROBOT,
            useCustomDriver,
            parameters: {
                fileType,
                fetchFileParameters: {
                    begin: startDate,
                    end: endDate,
                },
            },
        });
        return response.data;
    }
    /**
     * To cancel only UPLOAD file to Control Server/Robot requests which are in INITIATED status. These are the requests which require the frontend
     * to upload the file to S3 first and the user decided to cancel it before uploading the file to S3. But once the file is already uploaded
     * to S3 and the frontend already called 'updateUploadFileToControlServerRequestMetadata' to notify the backend, and backend is uploading the
     * files to the control server or robot, then don't use this API to cancel the request, use the 'cancelPendingFileRequest' function.
     * @param homeId
     * @param requestId
     */
    async cancelInitiatedFileUploadRequest(homeId, requestId) {
        await this.sendRequest(RequestMethod.PATCH, `homes/${homeId}/appProxy/rcp/fileProcessor/requests/${requestId}/c2d-browser`, {
            status: FileRequestStatus.CANCELLING,
        });
    }
    /**
     * To cancel any request that is in PENDING status. This can be used for Download/Upload requests to Control server/Robot.
     * @param homeId
     * @param requestId
     */
    async cancelPendingFileRequest(homeId, requestId) {
        await this.sendRequest(RequestMethod.PATCH, `homes/${homeId}/appProxy/rcp/fileProcessor/requests/${requestId}/start-cancel`);
    }
    /**
     * To delete a NON PENDING request. This can be used for Download/Upload requests to Control Server/Robot.
     * @param homeId
     * @param requestId
     */
    async deleteFileRequest(homeId, requestId) {
        await this.sendRequest(RequestMethod.DELETE, `homes/${homeId}/appProxy/rcp/fileProcessor/requests/${requestId}`);
    }
}
